import { type Dispatch, type SetStateAction, type FC, useState } from 'react';

import { useFormatter } from '@eneco/i18n';
import { Fields } from '@sitecore/types/OfferStep';

import { ProductCard } from './ProductCard_B';
import { FlowConsumerContextEnergy } from '../../../../context';
import { ExpandableOfferSection, Ribbon } from '../../../../shared/components';
import { constructTextualDataKey, getCashbackTexts } from '../../../../shared/helpers';
import { checkOnDiscountType, getOfferTitle } from '../../../../shared/helpers/sitecore';
import { useProductItemContent } from '../../../../shared/hooks';
import { EnergyType, Offer } from '../../../../shared/types';
import { PriceDetailsOverview, TOURibbon } from '../../../components';
import { getOfferPriceWithoutDiscount, getOfferSubtitle } from '../misc/util';

export interface OfferCardProps {
  offer: Offer;
  energyType?: EnergyType;
  textualData?: Fields;
  flowContext?: FlowConsumerContextEnergy;
  isOpen: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  isSelected?: boolean;
  onSelect?: () => void;
}

/**
 * Experiment exp-n-575 variant C offer card: identical to variant B (see {@link OfferCard_B}),
 * including the cashback ribbon, but the price never reflects the discount — a single, undiscounted
 * "Per maand" price with no "inclusief korting" headline or secondary "Termijnbedrag". Reuses the
 * shared {@link ProductCard}.
 */
export const OfferCard: FC<OfferCardProps> = ({
  offer,
  energyType = EnergyType.Electricity,
  textualData,
  flowContext,
  isOpen,
  setOpen,
  isSelected,
  onSelect,
}) => {
  const { format, currency } = useFormatter();
  const [costPeriod, setCostPeriod] = useState('costsMonthly');
  void costPeriod;

  const textualDataKey = checkOnDiscountType(
    constructTextualDataKey(offer.contractSpan, energyType),
    offer?.discountType,
  );

  const { getProductDetails } = useProductItemContent(textualData?.productData?.productDataList);
  const productDetails = getProductDetails(textualDataKey);

  if (!productDetails) {
    return null;
  }

  const { usps } = productDetails;

  const cashbackTexts = getCashbackTexts(
    offer?.costDiscounts?.cashback?.type,
    textualData?.promotionTexts,
    textualData?.giftData,
    offer?.costDiscounts?.cashback?.value,
  );

  return (
    <ProductCard
      id={offer.type ?? ''}
      title={getOfferTitle(offer, textualData?.productData, textualData?.productTypeLabels)}
      subtitle={getOfferSubtitle(offer, textualData?.productTypeLabels)}
      usps={usps}
      price={getOfferPriceWithoutDiscount(offer)}
      ribbon={
        cashbackTexts ? (
          <Ribbon
            size="small"
            emphasis="high"
            title={format(cashbackTexts.title, {
              [`${offer?.costDiscounts?.cashback?.type}`]: currency.euroNoFractionDigits(
                offer?.costDiscounts?.cashback?.value ?? 0,
              ),
            })}
            text={cashbackTexts.text}
            trigger={cashbackTexts?.trigger}
          />
        ) : null
      }
      touRibbon={<TOURibbon textualData={textualData} offer={offer} />}>
      <ExpandableOfferSection
        isOpen={isOpen}
        setOpen={setOpen}
        isSelected={isSelected}
        onSelect={onSelect}
        offer={offer}
        textualData={textualData}
        flowContext={flowContext}
        setCostPeriod={setCostPeriod}
        textualDataKey={textualDataKey}
        showProductName={false}
        showEndOfContractDialog={false}
        triggerPaddingY="4">
        {flowContext && <PriceDetailsOverview flowContext={flowContext} textualData={textualData} offer={offer} />}
      </ExpandableOfferSection>
    </ProductCard>
  );
};
